import { countryName, useServicesSettings } from '@church/data'
import { LEVEL_LABELS, type DefaultPlanningLevel } from '@church/permissions'
import { faAppleBrands, faFloppyDisk, faGlobe, faMusic, faPen, faUsers } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { ListSection } from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

/**
 * ForServices' church settings (docs/14), for a church-wide ForServices
 * Administrator: the Planning & people level everyone has by default, and
 * (when Apple Music is set up) the country songs are matched in. Each reads
 * at a glance; its pen opens a panel to change it.
 */
export const Route = createFileRoute('/_authed/_shell/settings/services')({
  component: ServicesSettings,
})

function ServicesSettings() {
  const settings = useServicesSettings()
  const [open, setOpen] = useState(false)
  const [storefrontOpen, setStorefrontOpen] = useState(false)
  if (settings.isPending) return <Spinner />
  if (!settings.data) return <ErrorNote error={settings.error} />
  const level = settings.data.defaultPlanningLevel
  const current = settings.choices.find((choice) => choice.level === level)

  return (
    <div className="max-w-2xl space-y-6">
      <ListSection
        title="ForServices"
        icon={faMusic}
        action={
          <IconButton
            icon={faPen}
            label="Change the default level"
            variant="secondary"
            onClick={() => setOpen(true)}
          />
        }
      >
        <Card className="flex items-start gap-4 p-5">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-content-brand">
            <Icon icon={faUsers} className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-wide text-content-subtle uppercase">
              Everyone’s Planning &amp; people level
            </p>
            <p className="mt-0.5 text-base font-medium text-content-default">
              {LEVEL_LABELS[level]}
            </p>
            {current && <p className="text-sm text-content-muted">{current.description}</p>}
          </div>
        </Card>
      </ListSection>
      <DefaultLevelPanel key={level} level={level} open={open} onClose={() => setOpen(false)} />
      {settings.data.appleMusicEnabled && (
        <>
          <ListSection
            title="Apple Music"
            icon={faAppleBrands}
            action={
              <IconButton
                icon={faPen}
                label="Change the Apple Music country"
                variant="secondary"
                onClick={() => setStorefrontOpen(true)}
              />
            }
          >
            <Card className="flex items-start gap-4 p-5">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-content-brand">
                <Icon icon={faGlobe} className="size-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-content-subtle uppercase">
                  Songs are matched in
                </p>
                <p className="mt-0.5 text-base font-medium text-content-default">
                  {countryName(settings.data.appleMusicStorefront)}
                </p>
                <p className="text-sm text-content-muted">
                  Apple Music’s catalog and previews differ by country.
                </p>
              </div>
            </Card>
          </ListSection>
          <StorefrontPanel
            key={settings.data.appleMusicStorefront}
            storefront={settings.data.appleMusicStorefront}
            open={storefrontOpen}
            onClose={() => setStorefrontOpen(false)}
          />
        </>
      )}
    </div>
  )
}

/** Choosing the default: each level with what it allows; Save top right. */
function DefaultLevelPanel({
  level,
  open,
  onClose,
}: {
  level: DefaultPlanningLevel
  open: boolean
  onClose: () => void
}) {
  const settings = useServicesSettings()
  const [chosen, setChosen] = useState(level)
  const formId = useId()
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (next) return
        setChosen(level)
        settings.update.reset()
        onClose()
      }}
      title="Everyone’s level"
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={settings.update.isPending}
          disabled={chosen === level}
        >
          Save
        </Button>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          settings.update.mutate({ defaultPlanningLevel: chosen }, { onSuccess: onClose })
        }}
      >
        <p className="text-sm text-content-muted">
          Everyone in the church has at least this. Access given to someone, or their role, can only
          add to it.
        </p>
        <fieldset className="divide-y divide-border-subtle">
          <legend className="sr-only">Planning &amp; people level</legend>
          {settings.choices.map((choice) => (
            <label key={choice.level} className="flex min-h-12 items-start gap-3 py-3">
              <input
                type="radio"
                name="level"
                className="mt-1 size-4 accent-brand-solid"
                checked={chosen === choice.level}
                onChange={() => setChosen(choice.level)}
              />
              <span className="min-w-0">
                <span className="block text-base text-content-default">{choice.label}</span>
                <span className="block text-sm text-content-muted">{choice.description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <ErrorNote error={settings.update.error} />
      </form>
    </SidePanel>
  )
}

/** The Apple Music country: a choice of storefronts; Save top right. */
function StorefrontPanel({
  storefront,
  open,
  onClose,
}: {
  storefront: string
  open: boolean
  onClose: () => void
}) {
  const settings = useServicesSettings()
  const [chosen, setChosen] = useState(storefront)
  const formId = useId()
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (next) return
        setChosen(storefront)
        settings.update.reset()
        onClose()
      }}
      title="Apple Music country"
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={settings.update.isPending}
          disabled={chosen === storefront}
        >
          Save
        </Button>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          settings.update.mutate({ appleMusicStorefront: chosen }, { onSuccess: onClose })
        }}
      >
        <Field
          label="Country"
          description="Songs matched automatically are matched again there; ones a person chose keep their recording."
        >
          <Select value={chosen} onChange={(event) => setChosen(event.target.value)}>
            {settings.storefronts(storefront).map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorNote error={settings.update.error} />
      </form>
    </SidePanel>
  )
}
